import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import { fetchMFAPreference } from '@aws-amplify/auth';
import { v4 as uuidv4 } from 'uuid';
import { toCourtISO, nyDateKey, nyTodayKey } from '../utils/datetime';

const AppContext = createContext();
const client = generateClient({ authMode: 'userPool' });

export const AppProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [requireMFASetup, setRequireMFASetup] = useState();

  const cache = useRef({
    cases: [],
    metrics: null,
    lastFetched: null,
  });

  useEffect(() => {
    const checkMFA = async () => {
      try {
        const pref = await fetchMFAPreference();
        const isEnabled = pref?.preferred === 'TOTP';
        setMfaEnabled(isEnabled);
        setRequireMFASetup(!isEnabled);
        console.log('🔐 MFA check:', { enabled: isEnabled, required: !isEnabled });
      } catch (err) {
        console.error('❌ Failed to fetch MFA status:', err);
        setRequireMFASetup(false);
      }
    };

    checkMFA();
  }, []);

  // Removed automatic status change logic - cases will maintain their status unless manually updated

  const getAllCases = async (forceRefresh = false) => {
    if (!forceRefresh && cache.current.cases.length > 0) {
      return cache.current.cases;
    }

    let all = [];
    let nextToken = null;

    do {
      const res = await client.models.Case.list({ limit: 1000, nextToken });
      all = [...all, ...res.data];
      nextToken = res.nextToken;
    } while (nextToken);

    cache.current.cases = all;
    cache.current.lastFetched = new Date();
    return all;
  };

  const updateCase = async (caseData) => {
    try {
      const id = caseData.id || uuidv4();

      // ✅ Only send fields that exist in the schema
      const schemaFields = [
        'id', 'firstName', 'lastName', 'dob', 'licenseNumber', 'phone',
        'secondaryPhone', 'tertiaryPhone', 'email', 'mailingAddress',
        'incidentCounty', 'caseType', 'officerName', 'appearanceNumber',
        'idnumber', 'courtDateTime', 'violationDate', 'disposition', 'status',
        'totalFee', 'paidAmount', 'amountDue', 'balanceDue', 'comments',
      ];

      const cleaned = {};
      schemaFields.forEach((key) => {
        if (caseData[key] !== undefined) {
          cleaned[key] = caseData[key];
        }
      });

      // 🔄 Normalize
      cleaned.id = id;
      cleaned.totalFee = parseFloat(cleaned.totalFee) || 0;
      cleaned.paidAmount = parseFloat(cleaned.paidAmount) || 0;
      cleaned.amountDue = parseFloat(cleaned.amountDue) || 0;
      cleaned.balanceDue = cleaned.balanceDue || 'NOT PAID';
      cleaned.status = cleaned.status || 'Open';

      // Court date/time is always interpreted/stored as New York time.
      if (cleaned.courtDateTime)
        cleaned.courtDateTime = toCourtISO(cleaned.courtDateTime);

      // violationDate is a date-only field; guard against unparseable values.
      if (cleaned.violationDate) {
        const vd = new Date(cleaned.violationDate);
        cleaned.violationDate = Number.isNaN(vd.getTime()) ? null : vd.toISOString();
      }

      const existing = await client.models.Case.get({ id });

      if (existing?.data) {
        await client.models.Case.update(cleaned);
        console.log('✅ Case updated:', cleaned);
      } else {
        await client.models.Case.create(cleaned);
        console.log('🆕 Case created:', cleaned);
      }

      cache.current.cases = [];
    } catch (err) {
      console.error('❌ updateCase failed:', err);
      throw err;
    }
  };

  const deleteCase = async (id) => {
    try {
      await client.models.Case.delete({ id });
      cache.current.cases = cache.current.cases.filter((c) => c.id !== id);
      console.log(`🗑️ Case ${id} deleted`);
    } catch (err) {
      console.error('❌ Error deleting case:', err);
      throw err;
    }
  };

  const importCases = async (caseList = []) => {
    try {
      await Promise.all(caseList.map((c) => updateCase(c)));
      console.log(`✅ Imported ${caseList.length} cases`);
    } catch (err) {
      console.error('❌ Bulk import failed:', err);
      throw err;
    }
  };

  const getDashboardMetrics = async () => {
    if (cache.current.metrics) return cache.current.metrics;

    let openCases = 0;
    let newCases = 0;
    let todayCourt = 0;
    let thisWeek = 0;

    const today = new Date();
    const weekLater = new Date();
    weekLater.setDate(today.getDate() + 7);
    const todayKeyNY = nyTodayKey();

    const cases = await getAllCases();

    for (const c of cases) {
      const status = (c.status || '').toLowerCase();
      const dt = c.courtDateTime ? new Date(c.courtDateTime) : null;

      if (status === 'open' || status === 'waiting on date') openCases++;
      if (status === 'new') newCases++;
      if (dt && nyDateKey(c.courtDateTime) === todayKeyNY) todayCourt++;
      if (dt && dt >= today && dt <= weekLater) thisWeek++;
    }

    const metrics = { openCases, newCases, todayCourt, thisWeek };
    cache.current.metrics = metrics;

    return metrics;
  };

  const getUpcomingCases = async () => {
    const all = await getAllCases();
    const today = new Date();

    return all
      .filter((c) => c.courtDateTime && new Date(c.courtDateTime) >= today)
      .sort((a, b) => new Date(a.courtDateTime) - new Date(b.courtDateTime))
      .slice(0, 7);
  };

  const getCaseById = async (id) => {
    const all = await getAllCases();
    return all.find((c) => c.id === id) || null;
  };

  // Removed checkAndUpdateMissedCases function - no longer automatically updating case statuses

  const refreshCache = async () => {
    cache.current = { cases: [], metrics: null, lastFetched: null };
    await getAllCases(true);
  };

  return (
    <AppContext.Provider
      value={{
        loading,
        updateCase,
        deleteCase,
        importCases,
        getCaseById,
        getDashboardMetrics,
        getUpcomingCases,
        getAllCases,
        refreshCache,
        mfaEnabled,
        setMfaEnabled,
        requireMFASetup,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => useContext(AppContext);
